import React, { useState } from 'react';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import { uploadedDocumentIds } from '../utils/uploadedDocumentIds';

const CustomQuizSection = ({ onQuizGenerated, setIsLoading }) => {
  const [specFile, setSpecFile] = useState(null);
  const [pastPaperFile, setPastPaperFile] = useState(null);
  const [cacheKey, setCacheKey] = useState(null);
  // The files the upload behind cacheKey saved; linked to every quiz
  // generated from it. Both are cleared when the student picks a new file.
  const [documentIds, setDocumentIds] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [quizStatus, setQuizStatus] = useState(null);
  const [numQuestions, setNumQuestions] = useState(3);
  const [topicFocus, setTopicFocus] = useState('');

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'spec') {
      setSpecFile(file);
    } else {
      setPastPaperFile(file);
    }
    // Upload once per pick: a new file needs a new upload before Generate.
    setCacheKey(null);
    setDocumentIds([]);
    setUploadStatus(null);
  };

  const uploadFiles = async () => {
    if (!specFile || !pastPaperFile) {
      setUploadStatus({ type: 'error', message: 'Please select both files.' });
      return;
    }
    // These files are already uploaded: generating again reuses them.
    if (cacheKey) return;

    try {
      setIsLoading(true);
      setUploadStatus({ type: 'info', message: 'Processing...' });
      
      const result = await quizGeneratorAPI.uploadFiles(specFile, pastPaperFile);
      
      if (result.success) {
        setCacheKey(result.cache_key);
        setDocumentIds(uploadedDocumentIds(result));
        setUploadStatus({ type: 'success', message: result.message });
      } else {
        setUploadStatus({ type: 'error', message: result.error });
      }
    } catch (error) {
      setUploadStatus({ type: 'error', message: 'Error: ' + error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const generateQuiz = async () => {
    if (!cacheKey) {
      setQuizStatus({ type: 'error', message: 'Upload files first.' });
      return;
    }

    try {
      setIsLoading(true);
      setQuizStatus({ type: 'info', message: 'Generating...' });
      
      const result = await quizGeneratorAPI.generateQuiz(cacheKey, parseInt(numQuestions), topicFocus, documentIds);
      
      if (result.success) {
        setQuizStatus({ type: 'success', message: 'Generated!' });
        onQuizGenerated(result.questions, result.session_id || null);
      } else {
        setQuizStatus({ type: 'error', message: result.error });
      }
    } catch (error) {
      if (error?.status === 422) {
        // A linked file is not the student's: upload the files again.
        setCacheKey(null);
        setDocumentIds([]);
        setQuizStatus(null);
        setUploadStatus({
          type: 'error',
          message: "We couldn't use one of your uploaded files for this quiz. Choose your files again, then Upload & Process.",
        });
      } else {
        setQuizStatus({ type: 'error', message: 'Error: ' + error.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="quiz-gen-main-content quiz-gen-main-content-active">
      <div className="quiz-gen-content-header">
        <h1>Custom Quiz Generator</h1>
        <p>Upload your materials to generate personalized practice questions</p>
      </div>

      <div className="quiz-gen-card">
        <div className="quiz-gen-form-group">
          <label htmlFor="specification">Exam Specification (PDF/TXT)</label>
          <div className="quiz-gen-file-input-wrapper">
            <input 
              type="file" 
              id="specification" 
              className="quiz-gen-file-input" 
              accept=".pdf,.txt"
              onChange={(e) => handleFileChange(e, 'spec')}
            />
            <div className={`quiz-gen-file-input-display ${specFile ? 'quiz-gen-file-input-has-file' : ''}`}>
              <div className="quiz-gen-file-input-button">{specFile ? '✓ Selected' : 'Choose file'}</div>
              <div className="quiz-gen-file-input-text">
                {specFile ? specFile.name : 'No file chosen'}
              </div>
            </div>
          </div>
        </div>
        
        <div className="quiz-gen-form-group">
          <label htmlFor="past_paper">Past Paper (PDF/TXT)</label>
          <div className="quiz-gen-file-input-wrapper">
            <input 
              type="file" 
              id="past_paper" 
              className="quiz-gen-file-input" 
              accept=".pdf,.txt"
              onChange={(e) => handleFileChange(e, 'paper')}
            />
            <div className={`quiz-gen-file-input-display ${pastPaperFile ? 'quiz-gen-file-input-has-file' : ''}`}>
              <div className="quiz-gen-file-input-button">{pastPaperFile ? '✓ Selected' : 'Choose file'}</div>
              <div className="quiz-gen-file-input-text">
                {pastPaperFile ? pastPaperFile.name : 'No file chosen'}
              </div>
            </div>
          </div>
        </div>
        
        <button onClick={uploadFiles} className="quiz-gen-btn quiz-gen-btn-primary">
          Upload & Process
        </button>
        
        {uploadStatus && (
          <div className={`quiz-gen-status quiz-gen-status-${uploadStatus.type}`}>
            {uploadStatus.message}
          </div>
        )}
      </div>

      {cacheKey && (
        <div className="quiz-gen-card">
          <h3 className="quiz-gen-card-title">Generate Questions</h3>
          
          <div className="quiz-gen-form-group">
            <label htmlFor="numQuestions">Number of Questions</label>
            <select 
              id="numQuestions"
              value={numQuestions}
              onChange={(e) => setNumQuestions(e.target.value)}
            >
              <option value="1">1 Question</option>
              <option value="2">2 Questions</option>
              <option value="3">3 Questions</option>
              <option value="5">5 Questions</option>
            </select>
          </div>
          
          <div className="quiz-gen-form-group">
            <label htmlFor="topicFocus">Topic Focus (Optional)</label>
            <input 
              type="text" 
              id="topicFocus" 
              placeholder="e.g., Calculus, Algebra..." 
              value={topicFocus}
              onChange={(e) => setTopicFocus(e.target.value)}
            />
          </div>
          
          <button onClick={generateQuiz} className="quiz-gen-btn quiz-gen-btn-primary">
            Generate Quiz
          </button>
          
          {quizStatus && (
            <div className={`quiz-gen-status quiz-gen-status-${quizStatus.type}`} style={{ marginTop: '16px' }}>
              {quizStatus.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomQuizSection;

